import { rtkq, RtkqRequest } from '../rtkq';
import { api } from '../api';
import {
  AddAnnouncementDto,
  AnnouncementDto,
  AnnouncementLookupDto,
  announcementsHttp,
  UpdateAnnouncementDto,
} from '@/core/http.announcements';
import { Paged, Paging } from '@/core';
import { SortResult } from '@/components';

export type AnnouncementState = AnnouncementDto;
export type AnnouncementLookupState = AnnouncementLookupDto;
export type AddAnnouncementArgs = AddAnnouncementDto;
export type UpdateAnnouncementArgs = UpdateAnnouncementDto;

const announcementsApi = api.injectEndpoints({
  endpoints: (build) => ({
    getAnnouncementList: build.query<AnnouncementState[], RtkqRequest<{ statuses?: string[] }>>({
      queryFn: async (args) => {
        return rtkq(args).exec(() => announcementsHttp.getAll(args.statuses));
      },
      providesTags: (result) => [
        { type: 'announcement', id: 'list' },
        ...(result || []).map(({ id }) => ({
          type: 'announcement' as const,
          id,
        })),
      ],
    }),

    searchAnnouncements: build.query<
      Paged<AnnouncementLookupState>,
      RtkqRequest<{
        searchString?: string;
        paging: Paging;
        sorting?: SortResult;
      }>
    >({
      queryFn: async (args) => {
        return rtkq(args).exec(() => announcementsHttp.search(args));
      },
      providesTags: (result) => [
        { type: 'announcement', id: 'list' },
        ...(result?.items || []).map(({ id }) => ({
          type: 'announcement' as const,
          id,
        })),
      ],
    }),

    getAnnouncementDetails: build.query<
      AnnouncementState,
      RtkqRequest<{ id: string }>
    >({
      queryFn: async (args) => {
        return await rtkq(args).exec(() => announcementsHttp.get(args.id));
      },
      providesTags: (announcement) => [
        { type: 'announcement', id: announcement?.id ?? 'none' },
      ],
    }),

    addAnnouncement: build.mutation<string, RtkqRequest<AddAnnouncementArgs>>({
      queryFn: (args) => {
        return rtkq(args).exec(() => announcementsHttp.create(args));
      },
      invalidatesTags: () => [{ type: 'announcement', id: 'list' }],
    }),

    updateAnnouncement: build.mutation<
      void,
      RtkqRequest<UpdateAnnouncementArgs>
    >({
      queryFn: (args) => {
        return rtkq(args).exec(() => announcementsHttp.update(args));
      },
      invalidatesTags: (_, __, args) => [
        { type: 'announcement', id: 'list' },
        { type: 'announcement', id: args.id },
      ],
    }),

    removeAnnouncement: build.mutation<void, RtkqRequest<{ id: string }>>({
      queryFn: (args) => {
        return rtkq(args).exec(() => announcementsHttp.remove(args.id));
      },
      invalidatesTags: () => [{ type: 'announcement', id: 'list' }],
    }),

    getPendingAnnouncements: build.query<AnnouncementState[], RtkqRequest<{}>>({
      queryFn: async (args) => {
        return rtkq(args).exec(() => announcementsHttp.getPending());
      },
      providesTags: (result) => [
        { type: 'pending-announcements', id: 'list' },
        ...(result || []).map(({ id }) => ({
          type: 'pending-announcements' as const,
          id,
        })),
      ],
    }),

    acceptAnnouncement: build.mutation<void, RtkqRequest<{ id: string }>>({
      queryFn: (args) => {
        return rtkq(args).exec(() => announcementsHttp.accept(args.id));
      },
      invalidatesTags: () => [{ type: 'pending-announcements', id: 'list' }],
    }),

    publishAnnouncement: build.mutation<void, RtkqRequest<{ id: string }>>({
      queryFn: (args) => {
        return rtkq(args).exec(() => announcementsHttp.publish(args.id));
      },
      invalidatesTags: (_, __, args) => [
        { type: 'announcement', id: 'list' },
        { type: 'announcement', id: args.id },
      ],
    }),
  }),
});

export const {
  useGetAnnouncementListQuery,
  useSearchAnnouncementsQuery,
  useGetAnnouncementDetailsQuery,
  useAddAnnouncementMutation,
  useUpdateAnnouncementMutation,
  useRemoveAnnouncementMutation,
  useGetPendingAnnouncementsQuery,
  useAcceptAnnouncementMutation,
  usePublishAnnouncementMutation,
} = announcementsApi;
